"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { api, ApiError, getApiErrorMessage } from "@/lib/api";
import type { ReadTransport } from "@/lib/read-transport";

interface UseApiResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** Re-fetch. Pass `{ silent: true }` to revalidate in the background without
   *  flipping `loading` (keeps the current data visible — no skeleton flash) AND
   *  without setting `error` on failure (keeps the last-good data on screen — the
   *  caller can react to the returned `false` instead, e.g. a toast). Resolves to
   *  `true` on success, `false` on failure. */
  refetch: (opts?: { silent?: boolean }) => Promise<boolean>;
  /** Patch the cached data in place (e.g. merge a lightweight live poll — ADR 0085)
   *  without a network round-trip. No-op after unmount. */
  mutate: (updater: (prev: T | null) => T | null) => void;
}

/**
 * Hook for GET requests — auto-fetches on mount and when path changes.
 */
export function useApi<T = unknown>(
  path: string | null,
  /**
   * ADR 0334 R2 — how to read. Defaults to the staff transport, so the 86 files
   * that call this with one argument are untouched.
   */
  read: ReadTransport = api,
): UseApiResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(!!path);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  // ADR 0214 D16 — two overlapping refetches (e.g. one per autosave PATCH) resolve in
  // arrival order; without this a slow #1 landing after #2 rolls `data` back to the
  // older doc. Only the newest request may write.
  const seqRef = useRef(0);

  const fetchData = useCallback(async (opts?: { silent?: boolean }): Promise<boolean> => {
    if (!path) return false;
    const seq = ++seqRef.current;
    // Non-silent (foreground): show the skeleton + clear any prior error. Silent (background poll):
    // touch neither, so the current data stays visible even while the request is in flight / if it fails.
    if (!opts?.silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const result = await read<T>(path);
      if (seq !== seqRef.current) return true; // a newer fetch is in flight / landed — stale, drop
      if (mountedRef.current) {
        setData(result);
        setLoading(false);
        setError(null); // a success clears a prior error (incl. after a failed silent poll recovers)
      }
      return true;
    } catch (err) {
      if (seq !== seqRef.current) return false;
      if (mountedRef.current) {
        setLoading(false);
        // Only a foreground fetch surfaces the error UI; a silent poll failure keeps the last-good data.
        // Same unwrapping as the mutation path below — the load-failure
        // screens print this string too.
        if (!opts?.silent) setError(getApiErrorMessage(err, String(err)));
      }
      return false;
    }
  /*
   * `read` is in the deps because the fetch uses it. Callers must pass a STABLE
   * function (a module-level one, or `useCallback`) — an inline arrow would
   * rebuild `fetchData` every render and re-fire the effect below forever. The
   * default `api` is module-level, so every existing caller is stable for free.
   */
  }, [path, read]);

  const mutate = useCallback((updater: (prev: T | null) => T | null) => {
    if (mountedRef.current) setData(updater);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void fetchData();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchData]);

  /*
   * A NULL PATH REPORTS NOTHING — it does not report the last answer.
   *
   * `fetchData` returns early when there is no path, so without this the state
   * from the previous path stayed and kept being rendered. The War Room held
   * `/jobs/tickets/:id/gps` after a job was deselected and went on painting that
   * job's blue GPS trail across the map; nine call sites pass
   * `cond ? path : null` and every one of them was holding a stale answer.
   *
   * DERIVED, not cleared in an effect. An effect would clear one render late —
   * long enough to paint the old data once more — and it would be a setState
   * inside an effect, which is a cascading render the lint rule rightly rejects.
   *
   * Only the null case is blanked. Moving from one path to another still shows
   * the previous data until the new response lands; that is deliberate
   * anti-flicker and a different thing entirely.
   */
  if (!path) return { data: null, loading: false, error: null, refetch: fetchData, mutate };

  return { data, loading, error, refetch: fetchData, mutate };
}

/**
 * Hook for mutations (POST/PATCH/DELETE) — call execute() manually.
 */
export function useMutation<TReq = unknown, TRes = unknown>(
  path: string,
  method: "POST" | "PATCH" | "DELETE" = "POST",
) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const execute = useCallback(
    async (body?: TReq): Promise<TRes | null> => {
      setLoading(true);
      setError(null);
      try {
        const result = await api<TRes>(path, {
          method,
          body: method !== "DELETE" ? body : undefined,
        });
        setLoading(false);
        return result;
      } catch (err) {
        /*
         * ADR 0362 — unwrap the body instead of showing `ApiError.message`.
         *
         * That message is `API 409: {"message":"…","error":"Conflict",…}`, and
         * every form in the app renders this string straight into the sheet —
         * so a refused save showed the user a JSON blob. `getApiErrorMessage`
         * is the helper the rest of the codebase already uses for exactly this
         * (`apiDelete` call sites pass it); the hook simply never did.
         *
         * Nothing reads the status out of this string, so unwrapping it loses
         * nothing a caller depends on.
         */
        setError(getApiErrorMessage(err, String(err)));
        setLoading(false);
        return null;
      }
    },
    [path, method],
  );

  return { execute, loading, error };
}
